/**
 * Deep links into Reports: `?view=reports&report=<name>[&report_view=<sub-tab>]`.
 *
 * The address names a report by the words on its button (`payroll`,
 * `production`, `time-clock` …) so a link reads the way the screen does; the
 * internal ids (`worker_performance`, `closed_invoice` …) are accepted too, so
 * older links and in-app callers keep working. Reports writes the open report
 * back into the address, so a reload or a shared link opens the same one.
 */

export type ReportId =
  | 'production'
  | 'worker_performance'
  | 'closed_invoice'
  | 'user_audit'
  | 'time_clock'
  | 'fraud_alerts'
  | 'deleted_work_orders'
  | 'billing'
  | 'performance'
  | 'pdr_panels';

/** The name each report has in the address, from its button label. */
export const REPORT_SLUGS: Record<ReportId, string> = {
  closed_invoice: 'invoices',
  worker_performance: 'payroll',
  production: 'production',
  performance: 'performance',
  time_clock: 'time-clock',
  user_audit: 'audit',
  deleted_work_orders: 'deleted-work-orders',
  pdr_panels: 'pdr-panels',
  fraud_alerts: 'fraud-alerts',
  billing: 'billing',
};

const BY_SLUG: Record<string, ReportId> = Object.fromEntries(
  (Object.entries(REPORT_SLUGS) as Array<[ReportId, string]>).map(([id, slug]) => [slug, id])
);

/** The report a link names (by slug or internal id), or undefined. */
export const reportIdFromParam = (value: string | null | undefined): ReportId | undefined => {
  if (!value) return undefined;
  const v = value.trim().toLowerCase();
  if (Object.prototype.hasOwnProperty.call(REPORT_SLUGS, v)) return v as ReportId;
  return Object.prototype.hasOwnProperty.call(BY_SLUG, v) ? BY_SLUG[v] : undefined;
};

/** The report and sub-tab an address asks for. */
export const reportParamsFromUrl = (params: URLSearchParams): { report?: ReportId; reportView?: string } => {
  const report = reportIdFromParam(params.get('report'));
  // A sub-tab means nothing without the report it belongs to.
  return { report, reportView: report ? params.get('report_view') || undefined : undefined };
};

/**
 * Point the address at a report (and sub-tab) on an in-app link, as a new
 * history entry so Back returns to the screen the link was on.
 */
export const writeReportAddress = (report: string, reportView?: string): void => {
  const id = reportIdFromParam(report);
  if (!id) return;
  try {
    const params = new URLSearchParams(window.location.search);
    params.set('view', 'reports');
    params.set('report', REPORT_SLUGS[id]);
    if (reportView) params.set('report_view', reportView);
    else params.delete('report_view');
    window.history.pushState({ view: 'reports' }, '', `${window.location.pathname}?${params.toString()}`);
  } catch {
    // Address sync is a convenience; never break navigation over it.
  }
};

/** Write a report's sub-tab into the address as it changes (Time Clock › Approvals). */
export const syncReportViewToUrl = (reportView: string): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') !== 'reports' || params.get('report_view') === reportView) return;
    params.set('report_view', reportView);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params.toString()}`);
  } catch {
    // Convenience only.
  }
};

/**
 * Write the open report into the address (replaceState: switching reports
 * isn't a new page for Back). Only while Reports is the open view.
 */
export const syncReportToUrl = (report: ReportId): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') !== 'reports') return;
    const slug = REPORT_SLUGS[report];
    if (params.get('report') === slug) return;
    // A sub-tab belongs to the report it was opened with.
    if (reportIdFromParam(params.get('report')) !== report) params.delete('report_view');
    params.set('report', slug);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params.toString()}`);
  } catch {
    // Address sync is a convenience; never break the screen over it.
  }
};
